import React, { useRef, useEffect } from 'react'
import { cn } from '@/lib/utils'
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import CalendarDayPopup from './CalendarDayPopup'
import { isWeekend, currencySymbol, formatRateSym } from '@/lib/calendar-utils'
import { hasTargetAdjustment } from '@/lib/target-utils'
import type { DayInfo, RoomType, RateData, OccupancyDay, ComparisonMode } from '@/types/calendar'

interface TimelineGridProps {
  /** Today AT THE PROPERTY (YYYY-MM-DD) — see calendar-utils#todayIso. */
  today: string
  daysInMonth: DayInfo[]
  roomTypes: RoomType[]
  visibleRoomTypeIds: number[]
  rates: Record<string, RateData>
  occupancyByDate: Record<string, OccupancyDay>
  propertyOccupancyByDate?: Record<string, { booked: number; available: number; occupancyPct: number; label?: string | null }>
  comparisonData: Record<string, number | null>
  comparisonMode: ComparisonMode
  currency: string
  onCellClick: (date: string, roomTypeId: number) => void
  adrData?: Record<string, { adr: number | null; lastYearAdr: number | null }>
  /** The property's backend-served canonical path — threaded to the day popup's target link. */
  propertyBasePath?: string
}

function TimelineGrid({
  daysInMonth,
  roomTypes,
  visibleRoomTypeIds,
  rates,
  occupancyByDate,
  propertyOccupancyByDate,
  comparisonData,
  comparisonMode,
  currency,
  onCellClick,
  adrData,
  propertyBasePath,
  today: propertyToday,
}: TimelineGridProps) {
  const visibleTypes = roomTypes.filter((rt) => visibleRoomTypeIds.includes(rt.id))
  const sym = currencySymbol(currency)
  const todayRef = useRef<HTMLTableRowElement>(null)
  useEffect(() => {
    todayRef.current?.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'instant' })
  }, [])

  if (visibleTypes.length === 0) {
    return (
      <div className="rounded-xl border border-border/60 bg-white p-10 text-center shadow-sm">
        <p className="text-sm text-muted-foreground">No room types selected.</p>
      </div>
    )
  }

  return (
    <TooltipProvider delayDuration={800} skipDelayDuration={0}>
      <div className="overflow-x-auto rounded-xl border border-border/60 bg-white shadow-sm">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 min-w-[120px] border-b border-r border-border/40 bg-white px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Date
              </th>
              {visibleTypes.map((rt) => (
                <th
                  key={rt.id}
                  className="min-w-[120px] border-b border-r border-border/30 bg-white px-3 py-3 text-center text-xs uppercase tracking-wider text-muted-foreground"
                >
                  <span className="font-semibold">{rt.name}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {daysInMonth.map((day, idx) => {
              const today = day.date === propertyToday
              const weekend = isWeekend(day.dayOfWeek)
              const occ = occupancyByDate[day.date]

              return (
                <tr key={day.date} ref={today ? todayRef : undefined} className={idx % 2 === 0 ? '' : 'bg-slate-50/30'}>
                  <td className={cn(
                    'sticky left-0 z-10 border-b border-r border-border/40 bg-white px-3 py-2 whitespace-nowrap',
                    today && 'bg-primary/5'
                  )}>
                    <div className="flex items-center gap-2">
                      <div>
                        <span className={cn(
                          'text-[10px] font-medium uppercase',
                          weekend ? 'text-primary/70' : 'text-muted-foreground'
                        )}>
                          {day.dayOfWeek}
                        </span>
                        <span className={cn(
                          'ml-1.5 text-sm font-bold',
                          today ? 'text-primary' : 'text-foreground'
                        )}>
                          {day.dayOfMonth}
                        </span>
                      </div>
                      {occ && (
                        <span className={cn(
                          'text-[10px] font-semibold tabular-nums',
                          occ.occupancyPct >= 80 ? 'text-emerald-600' :
                          occ.occupancyPct >= 60 ? 'text-amber-600' : 'text-slate-500'
                        )}>
                          {occ.occupancyPct}%
                          <span className="font-normal text-muted-foreground"> · {occ.booked}/{occ.available}</span>
                        </span>
                      )}
                    </div>
                  </td>
                  {visibleTypes.map((rt) => {
                    const key = `${rt.id}:${day.date}`
                    const rate = rates[key]
                    const comparison = comparisonData[key]

                    if (!rate) {
                      return (
                        <td key={rt.id} className="border-b border-r border-border/30 px-2 py-2 text-center text-muted-foreground/50">
                          &mdash;
                        </td>
                      )
                    }

                    const baseRate = rate.computationDetails?.base_rate_amount
                    const isAboveBase = baseRate != null && rate.rateAmount > baseRate
                    const isBelowBase = baseRate != null && rate.rateAmount < baseRate
                    const isOverride = rate.source === 'manual_override'
                    const hasTarget = hasTargetAdjustment(rate.computationDetails?.adjustments)

                    let compDiff: number | null = null
                    if (comparisonMode !== 'none' && comparison != null) {
                      compDiff = rate.rateAmount - comparison
                    }

                    return (
                      <td
                        key={rt.id}
                        className={cn(
                          'relative border-b border-r border-border/30 p-0',
                          rate.stopSell && 'bg-slate-200/80',
                          !rate.stopSell && isAboveBase && 'bg-emerald-50/80',
                          !rate.stopSell && isBelowBase && 'bg-rose-50/80',
                          isOverride && 'ring-2 ring-inset ring-blue-400/60',
                          today && !rate.stopSell && !isAboveBase && !isBelowBase && 'bg-primary/5'
                        )}
                      >
                        {hasTarget && !rate.stopSell && (
                          <span
                            title="Target adjusted this rate"
                            className="absolute bottom-1 right-1 inline-block h-1.5 w-1.5 rounded-full bg-teal-500 opacity-80"
                          />
                        )}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div
                              onClick={() => onCellClick(day.date, rt.id)}
                              className="flex flex-col items-center justify-center px-2 py-2 cursor-pointer transition-colors hover:brightness-95"
                            >
                              {rate.stopSell ? (
                                <span className="text-[11px] font-bold text-slate-500">STOP</span>
                              ) : (
                                <>
                                  <span className={cn(
                                    'text-[13px] font-semibold tabular-nums',
                                    isAboveBase ? 'text-emerald-700' : isBelowBase ? 'text-rose-700' : 'text-foreground'
                                  )}>
                                    {formatRateSym(rate.rateAmount, sym)}
                                  </span>
                                  {compDiff != null && (
                                    <span className={cn(
                                      'text-[9px] tabular-nums',
                                      compDiff > 0 ? 'text-emerald-600' : compDiff < 0 ? 'text-rose-600' : 'text-muted-foreground'
                                    )}>
                                      {compDiff > 0 ? '+' : compDiff < 0 ? '-' : ''}{formatRateSym(Math.abs(compDiff), sym)}
                                    </span>
                                  )}
                                  <div className={cn(
                                    'flex items-center gap-0.5 mt-0.5',
                                    rate.syncStatus === 'synced' ? 'text-emerald-600' :
                                    rate.syncStatus === 'pending' ? 'text-amber-500' : 'text-muted-foreground/50'
                                  )}>
                                    <span className={cn(
                                      'inline-block h-1 w-1 rounded-full',
                                      rate.syncStatus === 'synced' ? 'bg-emerald-500' :
                                      rate.syncStatus === 'pending' ? 'bg-amber-500' : 'bg-slate-300'
                                    )} />
                                    <span className="text-[9px] tabular-nums">
                                      {rate.pushedRateAmount != null ? formatRateSym(rate.pushedRateAmount, sym) : '--'}
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent
                            side="bottom"
                            sideOffset={4}
                            className="max-w-none overflow-visible border-none bg-transparent p-0 shadow-none"
                          >
                            <CalendarDayPopup
                              today={propertyToday}
                              date={day.date}
                              dayOfWeek={day.dayOfWeek}
                              currency={currency}
                              rate={rate}
                              occupancy={occ ? {
                                booked: occ.booked,
                                available: occ.available,
                                occupancyPct: occ.occupancyPct,
                                pickup7d: occ.pickup7d,
                                pickup7dRooms: occ.pickup7dRooms,
                              } : undefined}
                              propertyOccupancy={propertyOccupancyByDate?.[day.date]}
                              roomType={rt}
                              adr={adrData?.[day.date]?.adr}
                              lastYearAdr={adrData?.[day.date]?.lastYearAdr}
                              propertyBasePath={propertyBasePath}
                            />
                          </TooltipContent>
                        </Tooltip>
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </TooltipProvider>
  )
}

export default React.memo(TimelineGrid)
